import { all, takeEvery, call, put, fork, take, select } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import actions from './actions';
import authAction from '../auth/actions';
import merchantAction from '../merchant/actions';
import dashboardAction from '../dashboard/actions';
import accountOfficerAction from '../account_officer/actions';
import trxAction from '../transactions/actions';
import appAction from '../app/actions';
import toolAction from '../tools/actions';
import financeAction from '../finance/actions';

import * as mqtt from 'mqtt';
import { getEnv, filterDuplicateData } from '@iso/lib/helpers/utility';
import { MQTT_MODULE, MQTT_TYPE } from '@iso/containers/constants';
import moment from 'moment';

const { messageArrived } = trxAction;
function getOptions(mqOpts) {
  const url = [
    {
      protocol: mqOpts.protocol,
      host: mqOpts.host_1,
      port: mqOpts.port
    },
    {
      protocol: mqOpts.protocol,
      host: mqOpts.host_2,
      port: mqOpts.port
    }
  ];

  return {
    clientId: '',
    clean: true,
    keepalive: 300,
    connectTimeout: 4000,
    rejectUnauthorized: false,
    username: mqOpts.username,
    password: mqOpts.password,
    servers: url
  };
}

let client;
export function* connectMQTT() {
  yield takeEvery('CONNECT', function* () {
    const state = yield select();
    const userInfo = state.Auth.identity;
    const email = userInfo.email;
    const env = getEnv().get('env');
    const topicLogin = `/${env}/cosmos/sessions/${userInfo.username}`;
    const topicNotification = `/${env}/cosmos/notification/${userInfo.username}`;
    const topicAppNotification = `/${env}/cosmos/app-notification/${userInfo.username}`;

    // declare mqOpts variable
    let mqOpts = state.Auth.decodeValue;

    // if state is empty, get the encrypted creds from local storage
    if (!mqOpts) {
      const saved = localStorage.getItem('mqOpts');

      // if local storage contains the encrypted value, proceed with decode call
      if (saved) {
        try {
          mqOpts = saved;
          // decode call
          yield put({
            type: authAction.DECODE_VALUE,
            payload: {
              value: mqOpts,
              email: email
            }
          });

          // wait for decoding to finish
          const result = yield take([
            authAction.DECODE_VALUE_FAILED,
            authAction.DECODE_VALUE_SUCCESS
          ]);

          // if decoding failed, show error then return
          if (result.type === authAction.DECODE_VALUE_FAILED) {
            yield put({
              type: authAction.DECODE_VALUE_FAILED,
              message: 'Something went wrong. Please try again later.',
              error: 'INVALID_AUTH'
            });
            console.error('Something went wrong');
            return;
          }
          // put the decoded value in the declared mqOpts variable
          mqOpts = result.result;
        } catch (e) {
          console.error('Something went wrong', e);
          return;
        }
      }
    }

    // pass the decoded mqOpts to getOptions function to be used for connecting
    const options = getOptions(mqOpts);

    try {
      client = mqtt.connect({
        ...options,
        clientId: 'cosmos_ui_' + Math.random().toString(16)
      });
      client.on('error', error => {
        console.log('MQTT CONNECT FAILED', error);
      });

      client.on('connect', () => {
        client.subscribe([topicLogin, topicNotification, topicAppNotification]);
        console.log('MQTT CONNECT SUCCESS');
      });

      client.on('reconnect', () => {
        console.warn('Reconnecting to MQTT Server');
      });

      const eventResult = yield call(createMQTTEventHandler, client);

      while (true) {
        try {
          const result = yield take(eventResult);
          if (result.type) {
            if (result.payload.token) {
              yield put({
                type: authAction.LOGIN_VALIDATOR,
                token: result.payload.token
              });
              yield localStorage.setItem('mqttToken', result.payload.token);
            } else if (result.payload.module === 'IN_APP') {
              yield put({
                type: actions.RECEIVED_NOTIFICATION,
                result: result.payload
              });

              const reduxState = yield select();
              const accountNotificationData = reduxState.Dashboard.accountNotificationData;
              const reduxUnreadCount = reduxState.Dashboard.unreadCount;

              if (accountNotificationData && accountNotificationData.length > 0) {
                let newLog = result.payload;
                delete newLog['module'];
                accountNotificationData.unshift(newLog);

                yield put({
                  type: dashboardAction.PUSH_ACCOUNT_NOTIFICATION,
                  payload: filterDuplicateData(accountNotificationData),
                  unreadCount: reduxUnreadCount + 1
                });
              } else {
                yield put({
                  type: dashboardAction.FETCH_ACCOUNT_NOTIFICATION,
                  payload: {
                    pagination: { page: 0, limit: 10 }
                  }
                });
              }
            } else if (result.payload.module === MQTT_MODULE.ACCOUNT_NOTIFICATION) {
              const reduxState = yield select();
              const reduxNotificationData = reduxState.Dashboard.accountNotificationData;
              const reduxUnreadCount = reduxState.Dashboard.unreadCount;

              let newLog = result.payload;

              // UPDATING DATA
              if (result.payload.type === MQTT_TYPE.LEAD_MERCHANT_STATUS) {
                const leadManagementListData = reduxState.AccountOfficer.leadManagementListData;

                let idPayload = newLog.data_id;
                let currentList = leadManagementListData ? leadManagementListData : [];

                let findIndex = currentList.find(list => list.id === idPayload);

                if (findIndex) {
                  yield put({
                    type: accountOfficerAction.FETCH_LEAD_MANAGEMENT_LIST_DATA_BY_MQTT,
                    payload: null
                  });
                }
                delete newLog.data_id;
                delete newLog.type;
              }

              // PUSH NOTIFICATION DATA
              if (reduxNotificationData && reduxNotificationData.length > 0) {
                delete newLog.module;

                reduxNotificationData.unshift(newLog);

                yield put({
                  type: dashboardAction.PUSH_ACCOUNT_NOTIFICATION,
                  payload: filterDuplicateData(reduxNotificationData),
                  unreadCount: reduxUnreadCount ? reduxUnreadCount + 1 : 0
                });
              } else {
                yield put({
                  type: dashboardAction.FETCH_ACCOUNT_NOTIFICATION,
                  payload: {
                    pagination: { page: 0, limit: 10 }
                  },
                });
              }
            } else if (result.payload.module === MQTT_MODULE.SYSTEM_LOGS_NOTIFICATION) {
              const reduxState = yield select();
              const accountSystemLogsData = reduxState.Dashboard.accountSystemLogsData;
              const reduxUnreadCount = reduxState.Dashboard.systemLogsUnreadCount;
              if (accountSystemLogsData && accountSystemLogsData.length > 0) {
                let newLog = result.payload;
                delete newLog['module'];
                accountSystemLogsData.unshift(newLog);

                yield put({
                  type: dashboardAction.PUSH_ACCOUNT_SYSTEM_LOGS,
                  payload: filterDuplicateData(accountSystemLogsData),
                  unreadCount: reduxUnreadCount + 1
                });
              } else {
                yield put({
                  type: dashboardAction.FETCH_ACCOUNT_SYSTEM_LOGS,
                  payload: null,
                  pagination: { page: 0, limit: 10 }
                });
              }
            } else if (result.payload.module === MQTT_MODULE.SYSTEM_APP_NOTIFICATION) {
              const reduxState = yield select();
              const accountNotificationData = reduxState.Dashboard.accountNotificationData;
              const reduxUnreadCount = reduxState.Dashboard.unreadCount;

              if (accountNotificationData && accountNotificationData.length > 0) {
                let newLog = result.payload;
                delete newLog['module'];
                accountNotificationData.unshift(newLog);

                yield put({
                  type: dashboardAction.PUSH_ACCOUNT_NOTIFICATION,
                  payload: filterDuplicateData(accountNotificationData),
                  unreadCount: reduxUnreadCount + 1
                });
              } else {
                yield put({
                  type: dashboardAction.FETCH_ACCOUNT_NOTIFICATION,
                  payload: {
                    pagination: { page: 0, limit: 10 }
                  },
                });
              }
            } else if (result.payload.module === 'REMINDER_NOTIFICATION') {
              yield put({
                type: appAction.SET_NOTIFICATION_REMINDER,
                payload: {
                  ...result.payload
                }
              });
            } else if (result.payload.module === 'GENERATE_REPORT_STATUS') {
              yield put({
                type: appAction.GENERATE_REPORT_STATUS,
                payload: {
                  ...result.payload
                }
              });
            } else if (result.payload.module === 'GENERATE_SRF_TRANSACTION_REPORT') {
              yield put({
                type: appAction.GENERATE_SRF_TRANSACTION_REPORT_STATUS,
                payload: {
                  ...result.payload
                }
              });
            }
          }
        } catch (err) {
          console.error('socket error:', err);
        }
      }
    } catch (e) {
      yield put({
        type: actions.CONNECT_FAILED
      });
    }
  });
}

export function* disconnectMQTT() {
  yield takeEvery('DISCONNECT_MQTT', function* () {
    try {
      if (client.connected) {
        client.disconnect();
        client.loop_stop();
      }
    } catch (e) {
      yield put({
        type: actions.CONNECT_FAILED
      });
    }
  });
}

export function* connectToMqtt() {
  yield takeEvery('CONNECT_MQTT', function* ({ payload }) {
    console.log('PAYLOAD_MQTT', payload);
    try {
      // create event channel
      const state = yield select();
      let mqOpts = state.Auth.decodeValue;
      const options = getOptions(mqOpts);
      const eventResult = yield call(createMQTTEventHandlerNew, { options, ...payload });

      while (true) {
        const result = yield take(eventResult);

        if (result.type === 'CONNECT_SUCCESS') {
          console.log('MQTT connected', result.payload);

          // Dispatch Redux action if needed
          yield put({ type: actions.CONNECT_SUCCESS, payload: result.payload });

          // Now proceed with next steps
          break; // exit loop after successful connection
        } else if (result.type === 'CONNECT_FAILED') {
          console.error('MQTT connection failed', result.error);
          yield put({ type: actions.CONNECT_FAILED, error: result.error });
          break;
        }
      }

      while (true) {
        try {
          const result = yield take(eventResult);
          const state = yield select();
          console.log('MQTT_RESULT', result);
          if (result.type) {
            if (payload.module === MQTT_MODULE.MERCHANT_FILE_MANAGEMENT) {
              const reduxFileData = state.Merchant.fileDownloadData;
              let dataFromMQTT = reduxFileData && reduxFileData.length > 0 ? reduxFileData : [];

              if (result.payload && result.payload.res && result.payload.message) {
                const findFileExistence =
                  dataFromMQTT && dataFromMQTT.length > 0
                    ? dataFromMQTT.some(item => item.id === result.payload.res.id)
                    : false;
                if (!findFileExistence) {
                  dataFromMQTT.push(result.payload.res);
                }

                yield put({
                  type: merchantAction.DOWNLOAD_FILE_DATA_SUCCESS,
                  payload: dataFromMQTT,
                  message: result.payload.message,
                  loading: false
                });
              } else {
                yield put({
                  type: merchantAction.DOWNLOAD_FILE_DATA_FAILED,
                  payload: reduxFileData,
                  message: 'Failed to store file',
                  loading: false
                });
              }
            } else if (payload.module === MQTT_MODULE.REQUIREMENT_FILE_DOWNLOAD) {
              const reduxFileData = state.AccountOfficer.requirementFileData;
              let dataFromMQTT = reduxFileData && reduxFileData.length > 0 ? reduxFileData : [];

              if (result.payload && result.payload.res && result.payload.message) {
                const findFileExistence =
                  dataFromMQTT && dataFromMQTT.length > 0
                    ? dataFromMQTT.some(item => item.id === result.payload.res.id)
                    : false;
                if (!findFileExistence) {
                  dataFromMQTT.push(result.payload.res);
                }

                yield put({
                  type: accountOfficerAction.DOWNLOAD_REQUIREMENT_FILE_DATA_SUCCESS,
                  payload: dataFromMQTT,
                  message: result.payload.message,
                  loading: false
                });
              } else {
                yield put({
                  type: accountOfficerAction.DOWNLOAD_REQUIREMENT_FILE_DATA_SUCCESS,
                  payload: reduxFileData,
                  message: 'Failed to store file',
                  loading: false
                });
              }
            } else if (payload.module === MQTT_MODULE.PARSING_FILE_NOTIFIER) {
              const state = yield select();
              const reduxUploadedFileFilter = state.Tools.uploadedChannel;
              const paymentChannel = reduxUploadedFileFilter.p_channel;
              const reconciliationFilter = state.Transaction.reconciliationFilter;

              yield put({
                type: trxAction.PARSE_DATA_FILE_NOTIFIER,
                success: result.payload.success,
                message: result.payload.message
              });

              yield put({
                type: toolAction.FETCH_UPLOADED_FILES,
                payload: {
                  ...reduxUploadedFileFilter
                },
                pagination: {
                  page: 0,
                  limit: 10
                }
              });

              yield put({
                type: trxAction.FETCH_RECONCILIATION_DATA_BY_PCHANNEL,
                payload: reconciliationFilter
              });

              yield put({
                type: trxAction.FETCH_COLLATED_DATA_BY_PCHANNEL,
                payload: reconciliationFilter
              });

              yield put({
                type: trxAction.FETCH_SETTLEMENT_REPORT,
                payload: {
                  payment_channel: reconciliationFilter.payment_channel,
                  date: reconciliationFilter.date,
                  fileFilter: `${moment(reconciliationFilter.date).format('MMMM_DD_YYYY')}`
                },
                pagination: { page: 0, limit: 20, loadMore: false }
              });

              yield put({
                type: trxAction.FETCH_UNRECONCILED_REPORT_LIST,
                payload: {
                  payment_channel: reconciliationFilter.payment_channel,
                  date: reconciliationFilter.date,
                  fileFilter: `${moment(reconciliationFilter.date).format('MMMM_DD_YYYY')}`
                },
                pagination: { page: 0, limit: 20, loadMore: false }
              });

              if (paymentChannel) {
                const logPagination = state.Tools.currentItemLogPagination
                  ? state.Tools.currentItemLogPagination
                  : { page: 0, limit: 10 };
                yield put({
                  type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                  id: paymentChannel,
                  payload: {},
                  pagination: logPagination
                });
              }
            } else if (payload.module === MQTT_MODULE.PARSE_RECONCILE_FILE) {
              const state = yield select();
              const reconFileFilter = state.Finance.reconFilesFilter;
              const pchannelFilter = state.Finance.pchannelRecordFilter;
              const resourceFilter = state.Finance.resourceRecordFilter;

              console.log('RECON_FILE', reconFileFilter);
              console.log('PCHANNEL', pchannelFilter);
              console.log('RESOURCE', resourceFilter);

              yield put({
                type: trxAction.PARSE_DATA_FILE_NOTIFIER,
                success: result.payload.success,
                message: result.payload.message
              });

              yield put({
                type: financeAction.FETCH_RECON_FILES,
                payload: {
                  ...reconFileFilter,
                  page: 0,
                  page_size: 50
                }
              });

              yield put({
                type: financeAction.FETCH_PAYMENT_CHANNEL_RECORDS,
                payload: {
                  ...pchannelFilter,
                  page: 0,
                  page_size: 50
                }
              });

              yield put({
                type: financeAction.FETCH_RESOURCE_RECORDS,
                payload: {
                  ...resourceFilter,
                  page: 0,
                  page_size: 50
                }
              });
            } else if (payload.module === MQTT_MODULE.PARSING_FILE_NOTIFIER_REMITTANCE) {
              const state = yield select();
              const reduxUploadedFileFilter = state.Finance.uploadedChannel;

              yield put({
                type: trxAction.PARSE_DATA_FILE_NOTIFIER,
                success: result.payload.success,
                message: result.payload.message
              });

              if (!result.payload.bank_id) {
                yield put({
                  type: financeAction.FETCH_UPLOADED_FILES_REMITTANCE,
                  payload: {
                    ...reduxUploadedFileFilter
                  },
                  pagination: {
                    page: 0,
                    limit: 10
                  }
                });
              }
              

              break;
            } else if (payload.module === MQTT_MODULE.LIKE_CASE_DISCUSSION) {
              const reduxCurrentDicsussion = state.Transaction.caseDiscussionData;
              const dataObj = result.payload.data;
              if (reduxCurrentDicsussion && reduxCurrentDicsussion.length > 0 && payload) {
                let currentDiscussion = reduxCurrentDicsussion.findIndex(
                  item => item.id === dataObj.discussion_id
                );
                if (reduxCurrentDicsussion[currentDiscussion]) {
                  let currentItem =
                    reduxCurrentDicsussion[currentDiscussion] &&
                    reduxCurrentDicsussion[currentDiscussion].likes
                      ? reduxCurrentDicsussion[currentDiscussion].likes.findIndex(
                          item => item.id === dataObj.recipient_id
                        )
                      : false;
                  let currentData = reduxCurrentDicsussion;
                  if (
                    reduxCurrentDicsussion[currentDiscussion].likes[currentItem] &&
                    !dataObj.is_liked
                  ) {
                    currentData[currentDiscussion].likes = currentData[
                      currentDiscussion
                    ].likes.filter(item => item.id !== dataObj.recipient_id);

                    yield put({
                      type: trxAction.SET_DISCUSSION_VALUE,
                      payload: [...currentData]
                    });
                  } else if (
                    !reduxCurrentDicsussion[currentDiscussion].likes[currentItem] &&
                    dataObj.is_liked
                  ) {
                    currentData[currentDiscussion].likes.push({
                      id: dataObj.recipient_id,
                      account_name: dataObj.account_name
                    });
                    yield put({
                      type: trxAction.SET_DISCUSSION_VALUE,
                      payload: [...currentData]
                    });
                  }
                  yield put({
                    type: trxAction.LIKE_CURRENT_ID,
                    payload: ''
                  });
                }
              }
            } else if (payload.module === MQTT_MODULE.LIKE_LEAD_DISCUSSION) {
              const reduxCurrentDiscussion = state.AccountOfficer.leadDiscussionData;
              const dataObj = result.payload.data;
              if (reduxCurrentDiscussion && reduxCurrentDiscussion.length > 0 && payload) {
                let currentDiscussion = reduxCurrentDiscussion.findIndex(
                  item => item.id === dataObj.discussion_id
                );
                if (reduxCurrentDiscussion[currentDiscussion]) {
                  let currentItem =
                    reduxCurrentDiscussion[currentDiscussion] &&
                    reduxCurrentDiscussion[currentDiscussion].likes
                      ? reduxCurrentDiscussion[currentDiscussion].likes.findIndex(
                          item => item.id === dataObj.recipient_id
                        )
                      : false;
                  let currentData = reduxCurrentDiscussion;
                  if (
                    reduxCurrentDiscussion[currentDiscussion].likes[currentItem] &&
                    !dataObj.is_liked
                  ) {
                    currentData[currentDiscussion].likes = currentData[
                      currentDiscussion
                    ].likes.filter(item => item.id !== dataObj.recipient_id);

                    yield put({
                      type: accountOfficerAction.SET_LEAD_DISCUSSION_VALUE,
                      payload: [...currentData]
                    });
                  } else if (
                    !reduxCurrentDiscussion[currentDiscussion].likes[currentItem] &&
                    dataObj.is_liked
                  ) {
                    currentData[currentDiscussion].likes.push({
                      id: dataObj.recipient_id,
                      account_name: dataObj.account_name
                    });
                    yield put({
                      type: accountOfficerAction.SET_LEAD_DISCUSSION_VALUE,
                      payload: [...currentData]
                    });
                  }
                  yield put({
                    type: accountOfficerAction.LIKE_LEAD_DISCUSSION_CURRENT_ID,
                    payload: ''
                  });
                }
              }
            } else if (payload.module === MQTT_MODULE.GENERATE_MATCHED_TRX) {
              const reduxUploadedFileFilter = state.Tools.uploadedChannel;
              const paymentChannel = reduxUploadedFileFilter.p_channel;

              if (result && result.payload && result.payload.success) {
                const state = yield select();
                const collatedDataFilter = state.Transaction.collatedDataFilter;
                const reconciliationFilter = state.Transaction.reconciliationFilter;

                yield put({
                  type: trxAction.ANALYZE_COLLATE_DATA_BY_PCHANNEL_DATA_SUCCESS,
                  loading: false,
                  message: result.payload.message
                });
                if (reconciliationFilter) {
                  yield put({
                    type: trxAction.FETCH_RECONCILIATION_DATA_BY_PCHANNEL,
                    payload: {
                      ...reconciliationFilter,
                      pagination: {
                        page: 0,
                        limit: 20
                      }
                    }
                  });
                }

                if (paymentChannel) {
                  const logPagination = state.Tools.currentItemLogPagination
                    ? state.Tools.currentItemLogPagination
                    : { page: 0, limit: 10 };
                  yield put({
                    type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                    id: paymentChannel,
                    payload: {},
                    pagination: logPagination
                  });
                }
                if (collatedDataFilter) {
                  yield put({
                    type: trxAction.FETCH_COLLATED_DATA_BY_PCHANNEL,
                    payload: {
                      ...collatedDataFilter,
                      pagination: {
                        page: 0,
                        limit: 20
                      }
                    }
                  });
                }
                yield put({
                  type: trxAction.ANALYZE_COLLATE_DATA_GENERATING_LOADING,
                  loading: false
                });
              } else {
                yield put({
                  type: trxAction.ANALYZE_COLLATE_DATA_BY_PCHANNEL_DATA_FAILED,
                  message: result.payload.message
                    ? result.payload.message
                    : 'Failed to generate matched transactions',
                  loading: false
                });
                yield put({
                  type: trxAction.ANALYZE_COLLATE_DATA_GENERATING_LOADING,
                  loading: false
                });
                if (paymentChannel) {
                  const logPagination = state.Tools.currentItemLogPagination
                    ? state.Tools.currentItemLogPagination
                    : { page: 0, limit: 10 };
                  yield put({
                    type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                    id: paymentChannel,
                    payload: {},
                    pagination: logPagination
                  });
                }
              }
            } else if (payload.module === MQTT_MODULE.CHECK_UNMATCHED_TRANSACTIONS) {
              const reduxUploadedFileFilter = state.Tools.uploadedChannel;
              const paymentChannel = reduxUploadedFileFilter.p_channel;
              if (result && result.payload && result.payload.success) {
                const state = yield select();
                const collatedDataFilter = state.Transaction.collatedDataFilter;
                const reconciliationFilter = state.Transaction.reconciliationFilter;

                yield put({
                  type: trxAction.CHECK_COLLATE_DATA_SUCCESS,
                  loading: false,
                  message: 'Transactions successfully found.'
                });
                yield put({
                  type: trxAction.CHECK_COLLATE_DATA_MQTT_LOADING,
                  loading: false
                });
                if (paymentChannel) {
                  const logPagination = state.Tools.currentItemLogPagination
                    ? state.Tools.currentItemLogPagination
                    : { page: 0, limit: 10 };
                  yield put({
                    type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                    id: paymentChannel,
                    payload: {},
                    pagination: logPagination
                  });
                }
                if (reconciliationFilter) {
                  yield put({
                    type: trxAction.FETCH_RECONCILIATION_DATA_BY_PCHANNEL,
                    payload: {
                      ...reconciliationFilter,
                      pagination: {
                        page: 0,
                        limit: 20
                      }
                    }
                  });
                }
                if (collatedDataFilter) {
                  yield put({
                    type: trxAction.FETCH_COLLATED_DATA_BY_PCHANNEL,
                    payload: {
                      ...collatedDataFilter,
                      pagination: {
                        page: 0,
                        limit: 20
                      }
                    }
                  });
                }
              } else {
                yield put({
                  type: trxAction.CHECK_COLLATE_DATA_MQTT_LOADING,
                  loading: false
                });
                yield put({
                  type: trxAction.CHECK_COLLATE_DATA_FAILED,
                  message:
                    result && result.payload && result.payload.message
                      ? result.payload.message
                      : 'No transaction found.',
                  loading: false
                });
                if (paymentChannel) {
                  const logPagination = state.Tools.currentItemLogPagination
                    ? state.Tools.currentItemLogPagination
                    : { page: 0, limit: 10 };
                  yield put({
                    type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                    id: paymentChannel,
                    payload: {},
                    pagination: logPagination
                  });
                }
              }
            } else if (payload.module === MQTT_MODULE.GENERATED_DOWNLOAD_UNMATCHED_TRANSACTIONS) {
              yield put({
                type: trxAction.GENERATE_UNRECONCILED_REPORT_DATA_SUCCESS,
                loading: false,
                message: 'Your file is generated successfully.'
              });
              const state = yield select();
              const unreconciledReportFilter = state.Transaction.unreconciledReportFilter;
              const reduxUploadedFileFilter = state.Tools.uploadedChannel;
              const paymentChannel = reduxUploadedFileFilter.p_channel;
              if (unreconciledReportFilter && unreconciledReportFilter.date) {
                yield put({
                  type: trxAction.FETCH_UNRECONCILED_REPORT_LIST,
                  payload: {
                    payment_channel:
                      result && result.payload ? result.payload.payment_channel : null,
                    date: unreconciledReportFilter.date,
                    fileFilter: `${moment(unreconciledReportFilter.date).format('MMMM_DD_YYYY')}`
                  },
                  pagination: { page: 0, limit: 20, loadMore: false }
                });
              }
              if (paymentChannel) {
                const logPagination = state.Tools.currentItemLogPagination
                  ? state.Tools.currentItemLogPagination
                  : { page: 0, limit: 10 };
                yield put({
                  type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                  id: paymentChannel,
                  payload: {},
                  pagination: logPagination
                });
              }
            } else if (payload.module === MQTT_MODULE.GENERATE_DOWNLOAD_RECONCILED_REPORT) {
              yield put({
                type: trxAction.GENERATE_REPORT_DATA_SUCCESS,
                loading: false,
                message: 'Your file is generated successfully.'
              });
              const state = yield select();
              const reconciliationFilter = state.Transaction.reconciliationFilter;
              const reduxUploadedFileFilter = state.Tools.uploadedChannel;
              const paymentChannel = reduxUploadedFileFilter.p_channel;
              if (reconciliationFilter && reconciliationFilter.date) {
                yield put({
                  type: trxAction.FETCH_SETTLEMENT_REPORT,
                  payload: {
                    payment_channel:
                      result && result.payload ? result.payload.payment_channel : null,
                    date: reconciliationFilter.date,
                    fileFilter: `${moment(reconciliationFilter.date).format('MMMM_DD_YYYY')}`
                  },
                  pagination: { page: 0, limit: 20, loadMore: false }
                });
              }
              if (paymentChannel) {
                const logPagination = state.Tools.currentItemLogPagination
                  ? state.Tools.currentItemLogPagination
                  : { page: 0, limit: 10 };
                yield put({
                  type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                  id: paymentChannel,
                  payload: {},
                  pagination: logPagination
                });
              }
            } else if (payload.module === MQTT_MODULE.GENERATE_RECON_REPORT) {
              const reportFilesFilter = state.Finance.reportFilesFilter;
              yield put({
                type: financeAction.FETCH_REPORT_FILES,
                payload: reportFilesFilter
              });
            } else if (payload.module === MQTT_MODULE.GENERATE_MONTHLY_RECONCILED_REPORT) {
              yield put({
                type: trxAction.GENERATE_MONTH_REPORT_DATA_SUCCESS,
                loading: false,
                message: 'Your file is generated successfully.'
              });
              const state = yield select();
              const monthlySettlementReportFilter = state.Transaction.monthlySettlementReportFilter;
              const paymentChannels = monthlySettlementReportFilter
                ? monthlySettlementReportFilter.payment_channel
                : null;

              if (monthlySettlementReportFilter && monthlySettlementReportFilter.date) {
                yield put({
                  type: trxAction.FETCH_MONTH_SETTLEMENT_REPORT,
                  payload: {
                    payment_channel: paymentChannels,
                    date: monthlySettlementReportFilter.date,
                    fileFilter: monthlySettlementReportFilter.date
                  },
                  pagination: { page: 0, limit: 10, loadMore: false }
                });
              }
              if (paymentChannels) {
                const logPagination = state.Tools.currentItemLogPagination
                  ? state.Tools.currentItemLogPagination
                  : { page: 0, limit: 10 };
                yield put({
                  type: toolAction.FETCH_LOGS_BY_ITEM_ID,
                  id: paymentChannels,
                  payload: {},
                  pagination: logPagination
                });
              }
            } else if (payload.module === MQTT_MODULE.PARSING_BANK_UPLOAD_FILE_NOTIFIER) {
              yield put({
                type: trxAction.PARSE_DATA_FILE_NOTIFIER,
                success: result.payload.success,
                message: result.payload.message
              });
              if (result.payload && result.payload.bank_id) {
                yield put({
                  type: trxAction.FETCH_UPLOADED_BANK_FILES,
                  payload: {
                    bank_id: result.payload.bank_id
                  },
                  pagination: {
                    page: 0,
                    limit: 10
                  }
                });
              }

              yield put({
                type: dashboardAction.FETCH_ACCOUNT_SYSTEM_LOGS,
                payload: null,
                pagination: { page: 0, limit: 10 }
              });
            } else if (payload.module === MQTT_MODULE.DOWNLOAD_TRX) {
              // const currentData = state.Transaction.excelDownloadList;
              // console.log('currentData', currentData)
              // const updatedList = _.reject(currentData, { Id: result.payload.Id });
              if (result.payload.status !== 'No Transaction Found!') {
                // const newList = _.union([result.payload], updatedList);
                yield put({
                  type: trxAction.FETCH_EXCEL_DOWNLOAD_LIST,
                  payload: {
                    module: payload.module
                  }
                });
              } else {
                yield put({
                  type: trxAction.FETCH_EXCEL_DOWNLOAD_LIST,
                  payload: {
                    module: payload.module
                  }
                });
                yield put({
                  type: trxAction.DOWNLOAD_STATUS,
                  payload: 'No Transaction Found!'
                });
              }
            } else if (payload.module === MQTT_MODULE.PARSING_LEAD_FILE_NOTIFIER) {
              const state = yield select();
              const currentFilter = state.AccountOfficer.leadsMgmtFilter;

              yield put({
                type: trxAction.PARSE_DATA_FILE_NOTIFIER,
                success: result.payload.success,
                message: result.payload.message
              });

              if (result.payload.success) {
                yield put({
                  type: accountOfficerAction.FETCH_ALL_LEADS,
                  payload: currentFilter,
                  pagination: { page: 0, limit: 20, loadMore: false }
                });

                yield put({
                  type: accountOfficerAction.FETCH_ALL_UPLOADED_LEADS_FILE_DATA,
                  payload: {},
                  pagination: { page: 0, limit: 20 }
                });
              }

              yield put({
                type: toolAction.DONE_PARSING_LEADS_FILE_DATA
              });
            } else if (payload.module === MQTT_MODULE.GENERATE_SRF_TRANSACTION_REPORT) {
            } else if (payload.module === MQTT_MODULE.REMITTANCE_TRX_PROCESS) {
              yield put ({
                type: financeAction.REMITTANCE_ACTIONS_NOTIFIER,
                success: result.payload.success,
                message: result.payload.message
              })
            }
          }
        } catch (err) {
          console.error('socket error:', err);
        }
      }
    } catch (e) {
      yield put({
        type: actions.CONNECT_FAILED
      });
    }
  });
}

function createMQTTEventHandler(client) {
  return eventChannel(emitter => {
    client.on('message', (topic, message) => {
      const newPayload = JSON.parse(message.toString());
      // console.log('MQTT PAYLOAD', newPayload, topic);
      emitter(messageArrived(newPayload));
    });
    return () => false;
  });
}

function createMQTTEventHandlerNew(payload) {
  const options = payload.options ? payload.options : {};
  return eventChannel(emitter => {
    const client = mqtt.connect({
      ...options,
      clientId: 'cosmos_ui_' + Math.random().toString(16)
    });

    // On successful connection
    client.on('connect', () => {
      client.subscribe([payload.topic]);
      console.log('MQTT CONNECT SUCCESS');
      emitter({ type: 'CONNECT_SUCCESS', client, payload });
    });

    // On error
    client.on('error', error => {
      console.error('MQTT ERROR', error);
      emitter({ type: 'CONNECT_FAILURE', error });
    });

    client.on('reconnect', () => {
      console.warn('Reconnecting to MQTT Server');
    });

    // Handle messages
    client.on('message', (topic, message) => {
      const newPayload = JSON.parse(message.toString());
      emitter(messageArrived(newPayload));

      // client.unsubscribe([topic]);
    });
    return () => false;
  });
}

export default function* rootSaga() {
  yield all([fork(connectMQTT), fork(connectToMqtt), fork(disconnectMQTT)]);
}
